'use client'

import { Suspense, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { ApproverShell } from '@/components/approver'
import { ReviewNotificationBell } from '@/components/review/ReviewNotificationBell'
import { ReviewSidebar } from '@/components/review/ReviewSidebar'
import { useReviewSummary, useReviewUnreadCount } from '@/hooks/useReview'
import { AUTO_NAME, isNavItemActive } from '@/lib/navigation'
import { REVIEWER_HOME } from '@/lib/roles'
import { REVIEW_UPDATED_EVENT, roleChipLabel } from '@/lib/review-view'

/**
 * The reviewer-only shell (plan 04 section 3.1, A13): a thin wrapper that
 * feeds `ApproverShell` (plan 08 WP-VIS-06, master plan 4.12) with the
 * session name, the role chip from the reviewer directory ("Buyer", falling
 * back to "Approver"), the waiting count on "My approvals" and the
 * notification bell (A4). Email is never shown. Plan 10 C6 adds the approver
 * sidebar (briefing, "My approvals", "Approved by me") from the same summary poll.
 */
export function ReviewShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const summary = useReviewSummary()
  const unread = useReviewUnreadCount()
  const refreshSummary = summary.refresh
  const refreshUnread = unread.refresh

  // The reviewer pages announce a decision or an opened request; refresh the counts at once.
  useEffect(() => {
    const onUpdate = () => {
      void refreshSummary()
      void refreshUnread()
    }
    window.addEventListener(REVIEW_UPDATED_EVENT, onUpdate)
    return () => window.removeEventListener(REVIEW_UPDATED_EVENT, onUpdate)
  }, [refreshSummary, refreshUnread])

  const reviewer = summary.data?.reviewer ?? null
  const name = reviewer?.name || session?.user?.name || null

  return (
    <ApproverShell
      product={AUTO_NAME}
      homeHref={REVIEWER_HOME}
      homeLabel='My approvals'
      homeActive={isNavItemActive(pathname, REVIEWER_HOME)}
      homeBadge={summary.data?.waiting ?? 0}
      homeBadgeLabel='waiting'
      userName={name}
      roleLabel={roleChipLabel(reviewer)}
      mainId='review-main'
      sidebarLabel='Approver sidebar'
      sidebar={
        // The sidebar reads `?filter=` for its active item; Suspense keeps static pages buildable.
        <Suspense fallback={null}>
          <ReviewSidebar
            summary={summary.data}
            loading={summary.loading}
            error={summary.error}
            onRetry={() => void refreshSummary()}
          />
        </Suspense>
      }
      actions={<ReviewNotificationBell unread={unread.data ?? 0} onChanged={() => void refreshUnread()} />}
    >
      {children}
    </ApproverShell>
  )
}
